/**
 * Shared canvas utilities for audio timeline components
 * Ensures consistent rendering behavior across waveform and ruler canvases
 */

export const DEFAULT_MAX_CANVAS_WIDTH = 32000; // Increased for longer audio files

/**
 * Calculate the maximum canvas width based on zoom level (pixels per second)
 * Higher zoom levels allow larger canvas widths for better detail
 */
export const getAdaptiveMaxCanvasWidth = (pixelsPerSecond: number, baseMax: number = DEFAULT_MAX_CANVAS_WIDTH): number => {
  // At high zoom levels, allow larger canvas widths
  if (pixelsPerSecond > 200) {
    return 65000; // 65k pixels for extreme zoom
  } else if (pixelsPerSecond > 100) {
    return 48000; // 48k pixels for high zoom
  } else if (pixelsPerSecond > 50) {
    return 32000; // 32k pixels for medium zoom
  }
  return Math.max(baseMax, 32000); // At least 32k for low zoom
};

/**
 * Calculate the actual canvas width for a given visible duration and zoom level
 */
export const calculateCanvasWidth = (
  visibleDuration: number, 
  pixelsPerSecond: number, 
  baseMax: number = DEFAULT_MAX_CANVAS_WIDTH
): number => {
  const maxCanvasWidth = getAdaptiveMaxCanvasWidth(pixelsPerSecond, baseMax);
  return Math.min(maxCanvasWidth, visibleDuration * pixelsPerSecond);
};

/**
 * Calculate optimal tile size based on zoom level and audio duration
 * Larger tiles for longer audio, smaller tiles for higher zoom
 */
export const getOptimalTileSize = (pixelsPerSecond: number, totalDuration: number): number => {
  // Base tile size in seconds
  let tileSize = 30;
  
  // Adjust based on total duration
  if (totalDuration > 3600) { // > 1 hour
    tileSize = 120; // 2 minute tiles
  } else if (totalDuration > 1800) { // > 30 minutes
    tileSize = 60; // 1 minute tiles
  }
  
  // Adjust based on zoom level
  if (pixelsPerSecond > 200) {
    tileSize = Math.max(15, tileSize / 2); // Smaller tiles for high zoom
  } else if (pixelsPerSecond < 20) {
    tileSize = Math.min(300, tileSize * 2); // Larger tiles for low zoom
  }
  
  return tileSize;
};

/**
 * Estimate memory usage for a given number of tiles
 */
export const estimateTileMemoryUsage = (tileCount: number, tileWidth: number, tileHeight: number): number => {
  // Each pixel uses 4 bytes (RGBA), plus canvas overhead
  const bytesPerTile = tileWidth * tileHeight * 4;
  const overheadPerTile = 1024; // Approximate overhead per canvas
  return tileCount * (bytesPerTile + overheadPerTile);
};

/**
 * Calculate safe tile cache size based on available memory
 */
export const calculateSafeTileCacheSize = (tileWidth: number, tileHeight: number): number => {
  // Estimate available memory (conservative)
  const estimatedAvailableMemory = 100 * 1024 * 1024; // 100MB
  const maxMemoryForTiles = estimatedAvailableMemory * 0.1; // Use 10% for tiles
  
  const memoryPerTile = estimateTileMemoryUsage(1, tileWidth, tileHeight);
  const maxTiles = Math.floor(maxMemoryForTiles / memoryPerTile);
  
  // Clamp between reasonable bounds
  return Math.max(10, Math.min(50, maxTiles));
};

/**
 * Set up canvas for high-DPI rendering with proper CSS sizing
 */
export const setupCanvasForHighDPI = (
  canvas: HTMLCanvasElement, 
  width: number, 
  height: number
): CanvasRenderingContext2D => {
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');

  const dpr = window.devicePixelRatio || 1;
  
  // Set canvas internal size (for high-DPI rendering)
  canvas.width = width * dpr;
  canvas.height = height * dpr;
  
  // Set canvas CSS size (actual display size)
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  
  // Scale context for high-DPI rendering
  ctx.scale(dpr, dpr);
  
  return ctx;
};